import { GoogleGenerativeAI } from "@google/generative-ai";
import Cerebras from "@cerebras/cerebras_cloud_sdk";
import LOCAL_DB from "../constants/localDB";

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
export const PROVIDERS = [
  { name: "gemini",   fn: estimateWithGemini,   supportsImage: true },
  { name: "cerebras", fn: estimateWithCerebras, supportsImage: false },
];

const WORD_TO_NUMBER = {
  half: 0.5,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  a: 1,
  an: 1,
};

const WEIGHT_UNITS_PATTERN = /gram|( g )|g$|ml/;

const NUTRITION_PROMPT = (text) => `You are a nutritionist.
User ate: "${text}"

Return ONLY valid JSON with no markdown, no explanation:
{
  "items": [{ "name": "food", "cal": 100, "pro": 10, "per": "1 serving" }],
  "totalCal": 100,
  "totalPro": 10
}`;

const VISION_PROMPT = (text) => `You are a nutritionist analyzing a photo of food.
${text ? `User's description: "${text}"` : "Identify all food items in this image."}

Estimate the calories and protein for each item visible in the image.
Also create a short, natural meal title (2-5 words) summarizing the dish, e.g. "Grilled Chicken Salad".
Return ONLY valid JSON with no markdown, no explanation:
{
  "title": "Grilled Chicken Salad",
  "items": [{ "name": "food", "cal": 100, "pro": 10, "per": "1 serving" }],
  "totalCal": 100,
  "totalPro": 10
}`;

// ── PARSERS ───────────────────────────────────────────────────────────────────

/**
 * Parses quantity from a string like "2 eggs", "half banana", or "200g rice".
 * @param {string} text
 * @returns {number}
 */
export function parseQty(text) {
  const lower = text.toLowerCase();

  for (const [word, value] of Object.entries(WORD_TO_NUMBER)) {
    if (lower.startsWith(`${word} `)) return value;
  }

  const match = lower.match(/(\d+(\.\d+)?)/);
  if (!match) return 1;

  const val = parseFloat(match[0]);
  return WEIGHT_UNITS_PATTERN.test(lower) ? val / 100 : val;
}

/**
 * Splits a multi-food string into individual food entries.
 * @param {string} text
 * @returns {string[]}
 */
function splitFoodEntries(text) {
  return text
    .toLowerCase()
    .split(/[,\n]|\band\b/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Safely parses a JSON string, stripping markdown code fences if present.
 * @param {string} str
 * @returns {object|null}
 */
function safeParse(str) {
  try {
    return JSON.parse(str.replace(/```json|```/g, "").trim());
  } catch {
    return null;
  }
}

// ── LOCAL FALLBACK ────────────────────────────────────────────────────────────

/**
 * Estimates nutrition using the local keyword database.
 * Used as the final fallback when all AI providers fail.
 * @param {string} text
 * @returns {{ items: object[], totalCal: number, totalPro: number, isLocal: boolean }}
 */
export function estimateLocal(text) {
  const parts = splitFoodEntries(text);

  const items = parts.map((part) => {
    const qty = parseQty(part);
    const match = LOCAL_DB.find((f) => f.keywords.some((k) => part.includes(k)));

    if (match) {
      return {
        name: match.keywords[0],
        cal: Math.round(match.cal * qty),
        pro: Math.round(match.pro * qty),
        amount: part,
        per: match.per,
        qty,
      };
    }

    return { name: part, cal: 0, pro: 0, per: "unknown", qty, unknown: true };
  });

  return {
    items,
    totalCal: items.reduce((sum, i) => sum + i.cal, 0),
    totalPro: items.reduce((sum, i) => sum + i.pro, 0),
    isLocal: true,
  };
}

// ── AI PROVIDERS ──────────────────────────────────────────────────────────────

/**
 * Attempts nutrition estimation via Google Gemini (supports text + optional image).
 * @param {string} text
 * @param {{ base64: string, mimeType: string }|null} image
 * @returns {Promise<object|null>}
 */
async function estimateWithGemini(text, image = null) {
  const apiKey = process.env.REACT_APP_GEMINI_KEY;
  if (!apiKey) return null;

  const GEMINI_MODELS = [
    "gemini-2.5-flash",      // 20 RPD  — most accurate, try first
    "gemini-3.1-flash-lite-preview", // 500 RPD — high quota fallback
  ];

  const genAI = new GoogleGenerativeAI(apiKey);

  for (const modelId of GEMINI_MODELS) {
    try {
      console.log(`📡 Requesting Gemini: ${modelId}...`);
      const model = genAI.getGenerativeModel({ model: modelId });

      let result;
      if (image) {
        // Multimodal: image + text prompt
        const imagePart = {
          inlineData: { data: image.base64, mimeType: image.mimeType },
        };
        result = await model.generateContent([VISION_PROMPT(text), imagePart]);
      } else {
        result = await model.generateContent(NUTRITION_PROMPT(text));
      }

      const parsed = safeParse(result.response.text());

      if (parsed?.items) return { ...parsed, source: "gemini" };

      console.warn(`⚠️ ${modelId} returned unexpected format.`);
    } catch (err) {
      // 429 = quota exhausted — try next model
      // anything else = real error, still try next model but log it
      console.warn(`⚠️ ${modelId} failed (${err.status ?? err.message}) — trying next...`);
    }
  }

  return null; // both Gemini models exhausted
}

/**
 * Attempts nutrition estimation via Cerebras (LLaMA).
 * @param {string} text
 * @returns {Promise<object|null>}
 */
async function estimateWithCerebras(text) {
  const apiKey = process.env.REACT_APP_CEREBRAS_KEY;
  if (!apiKey) return null;

  try {
    console.log("📡 Requesting Cerebras...");

    const client = new Cerebras({ apiKey });

    const res = await client.chat.completions.create({
      model: "llama3.1-8b",
      messages: [{ role: "user", content: NUTRITION_PROMPT(text) }],
    });

    const parsed = safeParse(res.choices[0].message.content);

    if (parsed?.items) return parsed;

    console.warn("⚠️ Cerebras returned unexpected format.");
    return null;
  } catch (err) {
    console.error("❌ Cerebras failed:", err.message);
    return null;
  }
}

// ── ORCHESTRATION ─────────────────────────────────────────────────────────────

/**
 * Tries each AI provider in priority order, falling back to local estimation.
 * Priority: Gemini → Cerebras → Local DB
 * @param {string} text
 * @param {{ base64: string, mimeType: string }|null} image
 * @returns {Promise<object>}
 */
export async function estimateWithAI(text, image = null) {
  for (const { name, fn, supportsImage } of PROVIDERS) {
    // Skip providers that don't support images when image-only input
    if (image && !text && !supportsImage) continue;
    const result = await fn(text, supportsImage ? image : null);
    if (result) return { ...result, source: name };
  }
 
  console.log("⚠️ All AI providers failed. Falling back to local DB.");
  return estimateLocal(text || "unknown food"); // already has isLocal: true
}
 

// ── ENTRY POINT ───────────────────────────────────────────────────────────────

/**
 * Main entry point. Estimates nutrition for a given food description and/or image.
 * @param {string} text - e.g. "2 eggs, half banana, 200g rice"
 * @param {{ base64: string, mimeType: string }|null} image - optional food photo
 * @returns {Promise<{ items: object[], totalCal: number, totalPro: number }>}
 */
export async function estimateNutrition(text, image = null) {
  if (!text?.trim() && !image) {
    return { items: [], totalCal: 0, totalPro: 0 };
  }

  return estimateWithAI(text || "", image);
}